import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  collection,
  documentId,
  endBefore,
  getCountFromServer,
  getDocs,
  limit,
  limitToLast,
  orderBy,
  query,
  QueryEndAtConstraint,
  QueryFieldFilterConstraint,
  QueryLimitConstraint,
  QueryOrderByConstraint,
  QueryStartAtConstraint,
  startAfter,
  where,
  WhereFilterOp,
} from 'firebase/firestore';
import { map, Observable, Subscription } from 'rxjs';
import { collectionSnapshots, Firestore } from '../firebase/firestore';

/** Default page size for shared pagination (mirrors client Constants.PAGE_SIZE). */
export const DEFAULT_PAGE_SIZE = 20;

export interface QueryConfig {
  collectionPath: string; //  path to collection
  orderByField?: string; // field to orderBy
  order?: 'asc' | 'desc' | undefined; // asc, desc
  limit?: number; // limit per query
  customQuery?: CustomQuery[]; // additional query params {name: '', value: '', op?: ''}
  fullEqualitySearch?: boolean;
  isNext?: boolean;
  reset?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PaginationService implements OnDestroy {
  private subscription: Subscription = new Subscription();
  private firestore = inject(Firestore);

  private query!: QueryConfig;
  private readonly _count = signal(0);
  private readonly _data = signal<any[]>([]);
  private readonly _loading = signal(false);
  private readonly _page = signal(0);

  private prevCursor: any;
  private nextCursor: any;
  private pageSize = DEFAULT_PAGE_SIZE;

  /** Signal API (preferred for OnPush / templates). */
  readonly countSignal = this._count.asReadonly();
  readonly dataSignal = this._data.asReadonly();
  readonly loadingSignal = this._loading.asReadonly();
  readonly pageSignal = this._page.asReadonly();

  /** Observable API kept for existing `| async` templates. */
  loading: Observable<boolean> = toObservable(this._loading);
  count: Observable<number> = toObservable(this._count);
  data: Observable<any> = toObservable(this._data);

  get page(): number {
    return this._page();
  }
  set page(value: number) {
    this._page.set(value);
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  init({
    collectionPath,
    orderByField,
    customQuery,
    order,
    limit,
    fullEqualitySearch,
  }: QueryConfig) {
    this.pageSize = limit || DEFAULT_PAGE_SIZE;
    this.query = {
      collectionPath,
      orderByField,
      order: order || 'asc',
      limit: this.pageSize,
      customQuery,
      // init() always starts a new query (filters, page size, first load). Always clear
      // cursors/page so callers that omit reset:true cannot reuse a stale prevCursor.
      reset: true,
      fullEqualitySearch,
    };

    return this.doMakeRequest();
  }

  doMakeRequest = () => {
    this._loading.set(true);

    // Filter / refresh passes reset:true via init() without isNext. Clear cursors first so
    // we don't reuse prevCursor with endBefore against a new query (empty/wrong results).
    if (this.query.reset) {
      this.reset();
    }

    const constraints: (
      | QueryOrderByConstraint
      | QueryLimitConstraint
      | QueryStartAtConstraint
      | QueryEndAtConstraint
      | QueryFieldFilterConstraint
    )[] = [];

    let orderByOverride = '';

    if (this.query.customQuery && this.query.customQuery.length) {
      this.query.customQuery.forEach((q) => {
        if (typeof q.value === 'string' && q.op === '==') {
          if (this.query.fullEqualitySearch) {
            constraints.push(where(q.name, q.op, q.type === 'number' ? Number(q.value) : q.value));
          } else {
            constraints.push(where(q.name, '>=', q.value), where(q.name, '<=', q.value + '\uf8ff'));
            orderByOverride = q.name;
          }
        } else {
          constraints.push(where(q.name, q.op, q.value));
        }
      });
    }

    const colRef = collection(this.firestore, this.query.collectionPath);
    getCountFromServer(query(colRef, ...constraints)).then((snapshot) => {
      this._count.set(snapshot.data().count);
    });

    if (orderByOverride) {
      constraints.push(orderBy(orderByOverride, 'asc'));
    } else if (this.query.orderByField) {
      constraints.push(orderBy(this.query.orderByField, this.query.order));
    } else {
      // limitToLast (prev page) requires an orderBy; documentId keeps first/next/prev consistent
      // when callers omit orderByField.
      constraints.push(orderBy(documentId()));
    }

    if (this.query.isNext) {
      if (this.nextCursor) {
        constraints.push(startAfter(this.nextCursor), limit(this.pageSize));
      } else {
        constraints.push(limit(this.pageSize));
      }
    } else if (this.prevCursor) {
      constraints.push(endBefore(this.prevCursor), limitToLast(this.pageSize));
    } else {
      constraints.push(limit(this.pageSize));
    }

    return collectionSnapshots(query(colRef, ...constraints))
      .pipe(
        map((snapshots) =>
          snapshots.map((snapshot) => {
            return {
              ...snapshot.data(),
              docId: snapshot.id,
              doc: snapshot,
            } as any;
          }),
        ),
      )
      .subscribe((data) => {
        this.prevCursor = this.nextCursor = null;

        if (data.length) {
          this.prevCursor = data[0].doc;
          this.nextCursor = data[data.length - 1].doc;
        }

        this._data.set(data);
        this._loading.set(false);
      });
  };

  doExportData = async (config: QueryConfig): Promise<any[]> => {
    const { collectionPath, orderByField, customQuery, order, fullEqualitySearch } = config;

    const constraints: (
      | QueryOrderByConstraint
      | QueryFieldFilterConstraint
      | QueryStartAtConstraint
      | QueryEndAtConstraint
    )[] = [];

    let orderByOverride = '';

    if (customQuery && customQuery.length) {
      customQuery.forEach((q) => {
        if (typeof q.value === 'string' && q.op === '==') {
          if (fullEqualitySearch) {
            constraints.push(where(q.name, q.op, q.type === 'number' ? Number(q.value) : q.value));
          } else {
            constraints.push(where(q.name, '>=', q.value), where(q.name, '<=', q.value + '\uf8ff'));
            orderByOverride = q.name;
          }
        } else {
          constraints.push(where(q.name, q.op, q.value));
        }
      });
    }

    if (orderByOverride) {
      constraints.push(orderBy(orderByOverride, 'asc'));
    } else if (orderByField) {
      constraints.push(orderBy(orderByField, order || 'asc'));
    }

    const colRef = collection(this.firestore, collectionPath);

    return getDocs(query(colRef, ...constraints)).then((snapshots) =>
      snapshots.docs.map((snapshot) => {
        return {
          ...snapshot.data(),
          docId: snapshot.id,
          doc: snapshot,
        } as any;
      }),
    );
  };

  prevPage(isReset = false) {
    if (isReset) this.reset();
    else {
      this._page.update((p) => p - 1);
      this.query.isNext = false;
    }
    this.subscription.add(this.doMakeRequest());
  }

  nextPage() {
    this._page.update((p) => p + 1);
    this.query.isNext = true;
    this.subscription.add(this.doMakeRequest());
  }

  reset() {
    this._page.set(0);
    this.prevCursor = null;
    this.nextCursor = null;
    this.query.reset = false;
    this.query.isNext = undefined;
  }
}

export class CustomQuery {
  name: string;
  value: any;
  op: WhereFilterOp = '==';
  type = 'string';

  constructor(name: string, value: any, op: WhereFilterOp = '==', type = 'string') {
    this.name = name;
    this.value = value;
    this.op = op;
    this.type = type;
  }
}
